import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        // Construct base URL from apiUrl configured in Constants.ts
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        
        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface EnrollmentCreate {
    email: string
    username?: string
    password: string
    full_name?: string
    product_id: string
    product_type: 'course' | 'bundle' | 'digital_product'
    payment_method_id: string
    login_mode?: boolean
    discount_code?: string
}

export interface AuthenticatedEnrollmentCreate {
    product_id: string
    product_type: 'course' | 'bundle' | 'digital_product'
    payment_method_id: string
    discount_code?: string
}

export interface Enrollment {
    id: string
    user_id: string
    course_id?: string
    product_id: string
    product_type: 'course' | 'bundle' | 'digital_product'
    product_title: string
    purchase_price: number
    currency: string
    enrolled_at: string
    access_expires_at?: string
    status: 'active' | 'expired' | 'refunded' | 'cancelled'
    progress_percentage: number
    certificate_issued: boolean
    completed_at?: string
    last_accessed_at?: string
}

export interface EnrollmentWithProduct extends Enrollment {
    product_details?: {
        id: string
        title: string
        description: string
        thumbnail_url?: string
    }
}

export interface EnrollmentSuccess {
    enrollment: Enrollment
    access_token: string
    token_type: string
    user: {
        id: string
        email: string
        username: string
        full_name: string
        role: string
    }
    message: string
}

export interface ProgressUpdate {
    progress_percentage: number
}

export interface AccessCheck {
    has_access: boolean
}

// ============================================================================
// Service Functions
// ============================================================================

export const enrollmentService = {
    createEnrollment,
    createAuthenticatedEnrollment,
    getMyEnrollments,
    updateProgress,
    checkAccess,
}

/**
 * Create a new enrollment with payment processing
 * This creates a user account (or uses existing), processes payment, and creates enrollment
 */
async function createEnrollment(enrollmentData: EnrollmentCreate): Promise<ApiResponse<EnrollmentSuccess>> {
    try {
        const response = await getApiInstance().post('/enrollments/', enrollmentData)
        const responseData = response.data
        
        // Backend wraps response in ResponseModel: { success, message, data }
        // The actual enrollment data is in response.data.data
        let enrollmentSuccess: EnrollmentSuccess
        
        if (responseData.success && responseData.data) {
            // Wrapped response
            enrollmentSuccess = responseData.data
        } else if (responseData.data) {
            // Partially wrapped
            enrollmentSuccess = responseData.data
        } else {
            // Direct response (fallback)
            enrollmentSuccess = responseData
        }
        return {
            success: true,
            data: enrollmentSuccess
        }
    } catch (error: any) {
        console.error('Error creating enrollment:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to complete enrollment'
        }
    }
}

/**
 * Create enrollment for an already authenticated user
 * This skips user creation/login and only processes payment and enrollment
 */
async function createAuthenticatedEnrollment(enrollmentData: AuthenticatedEnrollmentCreate): Promise<ApiResponse<EnrollmentSuccess>> {
    try {
        const response = await getApiInstance().post('/enrollments/authenticated', enrollmentData)
        const responseData = response.data

        // Backend wraps response in ResponseModel: { success, message, data }
        // The actual enrollment data is in response.data.data
        let enrollmentSuccess: EnrollmentSuccess

        if (responseData.success && responseData.data) {
            // Wrapped response
            enrollmentSuccess = responseData.data
        } else if (responseData.data) {
            // Partially wrapped
            enrollmentSuccess = responseData.data
        } else {
            // Direct response (fallback)
            enrollmentSuccess = responseData
        }
        return {
            success: true,
            data: enrollmentSuccess
        }
    } catch (error: any) {
        console.error('Error creating authenticated enrollment:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to complete enrollment'
        }
    }
}

/**
 * Get current user's enrollments (purchased courses)
 */
async function getMyEnrollments(): Promise<ApiResponse<EnrollmentWithProduct[]>> {
    try {
        const response = await getApiInstance().get('/enrollments/my-courses')
        const data = response.data
        
        // Handle wrapped response from backend
        let enrollments: EnrollmentWithProduct[] = []
        
        if (data.success && data.data) {
            enrollments = data.data
        } else if (data.data) {
            enrollments = data.data
        } else if (Array.isArray(data)) {
            enrollments = data
        }
        
        return {
            success: true,
            data: enrollments
        }
    } catch (error: any) {
        console.error('Error fetching enrollments:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to fetch enrollments'
        }
    }
}

/**
 * Update course progress for an enrollment
 */
async function updateProgress(
    enrollmentId: string,
    progressData: ProgressUpdate
): Promise<ApiResponse<Enrollment>> {
    try {
        const response = await getApiInstance().put(
            `/enrollments/${enrollmentId}/progress`,
            progressData
        )
        const data = response.data
        
        // Handle wrapped response
        let enrollment: Enrollment
        if (data.success && data.data) {
            enrollment = data.data
        } else if (data.data) {
            enrollment = data.data
        } else {
            enrollment = data
        }
        
        return {
            success: true,
            data: enrollment
        }
    } catch (error: any) {
        console.error('Error updating progress:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to update progress'
        }
    }
}

/**
 * Check if user has access to a specific product
 */
async function checkAccess(
    productId: string,
    productType: 'course' | 'bundle' | 'digital_product'
): Promise<ApiResponse<AccessCheck>> {
    try {
        const response = await getApiInstance().get(
            `/enrollments/check-access/${productId}/${productType}`
        )
        const data = response.data
        
        // Handle wrapped response
        let accessData: AccessCheck
        if (data.success && data.data) {
            accessData = data.data
        } else if (data.data) {
            accessData = data.data
        } else {
            accessData = data
        }
        
        return {
            success: true,
            data: accessData
        }
    } catch (error: any) {
        console.error('Error checking access:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to check access'
        }
    }
}

export default enrollmentService
